import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ReactComponent as CHEVRON_DOWN } from "assets/icons/chevron_down_white.svg";
import { ReactComponent as CHEVRON_UP } from "assets/icons/chevron_up_white.svg";
import type { RoutesProp } from "api/interfaces/menuInterface.interface";
import { useLocation } from "react-router-dom";
import { useAppSelector } from "redux/hooks";
import { useProSidebar } from "react-pro-sidebar";
import * as s from "../HeaderNavBarStyled";
import * as ps from "./SubStyled";
import { useMenu } from "hook/useMenu";

interface Props {}

export default function SubMenuContainer(props: Props) {
  const location = useLocation();
  const horizontalScrollRef = useRef<HTMLDivElement>(null);
  const menuContainerRef = useRef<HTMLDivElement>(null);
  const [dropdownMenus, setDropdownMenus] = useState<number>(0);
  const resizeTimeout = useRef<NodeJS.Timeout | null>(null);
  const selectedAccount = useAppSelector((state) => state.accountSettings);
  const { collapsed } = useProSidebar();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [offsetLeft, setOffsetLeft] = useState<number>(0);
  const dropdownMenuRef = useRef<HTMLUListElement>(null);
  const moreRef = useRef<HTMLButtonElement>(null);
  const [subItemWidths, setSubItemWidths] = useState<number[]>([]);
  const { currentMenu, menuNavigation } = useMenu({});

  const submenu = useMemo(() => {
    if (currentMenu?.routeName === "/maintenance") {
      return [];
    }
    return currentMenu?.children || [];
  }, [currentMenu?.routeName, currentMenu?.children]);
  const slicedSubmenu: RoutesProp[] = useMemo(
    () => submenu.slice(0, submenu.length - dropdownMenus),
    [submenu, dropdownMenus]
  );

  const handleDropdownMenus = useCallback(() => {
    if (subItemWidths.length === 0) {
      return;
    }
    if (resizeTimeout.current) {
      clearTimeout(resizeTimeout.current);
    }
    resizeTimeout.current = setTimeout(() => {
      const containerWidth: number =
        horizontalScrollRef.current?.getBoundingClientRect().width || 0;
      const moreButtonWidth: number =
        moreRef.current?.getBoundingClientRect().width || 76;

      let totalWidthWithoutMore = 0;
      let visibleWithoutMore = 0;

      // Pass 1: ignore More button, detect overflow
      for (let i = 0; i < subItemWidths.length; i++) {
        if (totalWidthWithoutMore + subItemWidths[i] <= containerWidth) {
          totalWidthWithoutMore += subItemWidths[i];
          visibleWithoutMore++;
        } else {
          break;
        }
      }

      const hasOverflow = visibleWithoutMore < subItemWidths.length;
      let visibleItems = visibleWithoutMore;

      // Pass 2: if overflow exists, reserve More button width and recalc
      if (hasOverflow) {
        let totalWidthWithMore = 0;
        visibleItems = 0;
        for (let i = 0; i < subItemWidths.length; i++) {
          const reservedMoreWidth =
            i < subItemWidths.length - 1 ? moreButtonWidth : 0;
          if (
            totalWidthWithMore + subItemWidths[i] + reservedMoreWidth <=
            containerWidth
          ) {
            totalWidthWithMore += subItemWidths[i];
            visibleItems++;
          } else {
            break;
          }
        }
      }

      setDropdownMenus(Math.max(0, submenu.length - visibleItems));
      setIsOpen(false);
    }, 150);
  }, [subItemWidths, submenu.length]);

  const handleMenuClick = (item: RoutesProp, index: number) => {
    if (item.routeName) {
      menuNavigation({
        to: item.routeName,
      });
    }
  };
  const renderMenuItem = (
    item: RoutesProp,
    index: number,
    isPopup: boolean = false
  ) => {
    const isCurrent = location.pathname.includes(item.routeName || "");

    if (isPopup) {
      return (
        <Fragment key={index}>
          <ps.PopupMenu
            disabled={false}
            isCurrent={isCurrent}
            onClick={() => handleMenuClick(item, index)}
            data-testid="blocks-headernav-sub-popup-menu-menu"
          >
            {item.name}
          </ps.PopupMenu>
        </Fragment>
      );
    }

    return (
      <s.MenuItem
        level={selectedAccount.accountLevel}
        key={index}
        isCurrent={isCurrent}
        data-submenu-item="true"
        onClick={() => handleMenuClick(item, index)}
        data-testid={`blocks-headernav-sub-menu-item-menu-${
          item?.name?.toLowerCase().replace(" ", "-") || ""
        }`}
      >
        {item?.name}
      </s.MenuItem>
    );
  };

  useLayoutEffect(() => {
    setIsOpen(false);
    // Always measure with all submenu items visible first.
    setDropdownMenus(0);

    const rafId = requestAnimationFrame(() => {
      const itemNodes = menuContainerRef.current?.querySelectorAll(
        `button[data-submenu-item="true"]`
      );
      const widths = Array.from(itemNodes || []).map(
        (node) => (node as HTMLButtonElement).getBoundingClientRect().width || 0
      );
      setSubItemWidths(widths);
    });

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [location.pathname, submenu]);

  useEffect(() => {
    handleDropdownMenus();
    window.addEventListener("resize", handleDropdownMenus);
    const resizeObserver =
      typeof ResizeObserver !== "undefined" && horizontalScrollRef.current
        ? new ResizeObserver(() => {
            handleDropdownMenus();
          })
        : null;

    if (resizeObserver && horizontalScrollRef.current) {
      resizeObserver.observe(horizontalScrollRef.current);
    }

    return () => {
      window.removeEventListener("resize", handleDropdownMenus);
      resizeObserver?.disconnect();
      if (resizeTimeout.current) {
        clearTimeout(resizeTimeout.current);
      }
    };
  }, [subItemWidths, collapsed, handleDropdownMenus]);

  if (submenu.length === 0) return null;

  return (
    <>
      <s.MenuContainer ref={horizontalScrollRef}>
        <s.MenuInnerContainer ref={menuContainerRef}>
          {slicedSubmenu.map((item, index) => renderMenuItem(item, index))}

          {submenu.length > 0 && dropdownMenus > 0 && (
            <s.MenuItem
              isCurrent={false}
              isOpen={isOpen}
              level={selectedAccount.accountLevel}
              onClick={(e) => {
                setIsOpen(!isOpen);
                setOffsetLeft(e.currentTarget.offsetLeft || 0);
              }}
              ref={moreRef}
              data-testid="blocks-headernav-sub-menu-item-set-is-open"
            >
              More {isOpen ? <CHEVRON_UP /> : <CHEVRON_DOWN />}
            </s.MenuItem>
          )}
        </s.MenuInnerContainer>
      </s.MenuContainer>

      {isOpen && dropdownMenus > 0 && (
        <ps.PopupWrapper
          key="subMenuPopupWrapper"
          style={{
            left: offsetLeft,
            right: "auto",
            marginTop: -5,
            minWidth: 150,
          }}
          ref={dropdownMenuRef}
        >
          {submenu
            .slice(submenu.length - dropdownMenus)
            .map((item, index) => renderMenuItem(item, index, true))}
        </ps.PopupWrapper>
      )}
    </>
  );
}
